import type { Tone } from './state.ts';

/* ==========================================================================
   `processing_state`, in one place.

   The same enum was rendered by two vocabularies. `/fleet/detail` printed
   `copc` / `processing` and toned processing with `charging` — a ROBOT MODE
   colour (teal, "replenishing") spent on a PIPELINE state; `/fleet/scan`
   printed `COPC` / `Processing` and toned processing blue. Both casings then
   appeared in one viewport: `/fleet/scan/49` showed the badge `COPC` four
   times while its own Artefacts table said `copc`.

   One map, four surfaces (dashboard, robot detail, scan viewer, teleop).

   LOWERCASE, because it is the wire value and it is what three of the four
   surfaces already print. Naming a machine value in Title Case invites the
   reader to think it is a sentence the console wrote.
   ========================================================================== */

export type ProcessingState = 'raw' | 'processing' | 'copc' | 'failed' | null;

export interface ScanStateDesc {
  /** The wire value, as printed. */
  label: string;
  tone: Tone;
  hint: string;
}

const STATES: Record<string, ScanStateDesc> = {
  raw: {
    label: 'raw',
    tone: 'neutral',
    hint: 'Registered and stored. No point-cloud derivative has been requested.',
  },
  processing: {
    label: 'processing',
    tone: 'ok',
    hint: "The COPC worker is deriving a point cloud from this session's bags.",
  },
  copc: {
    label: 'copc',
    tone: 'good',
    hint: 'A cloud-optimised point cloud has been derived and is readable.',
  },
  failed: {
    label: 'failed',
    tone: 'error',
    hint: 'The COPC worker could not derive a point cloud from this capture.',
  },
};

const UNRECORDED: ScanStateDesc = {
  label: 'unrecorded',
  tone: 'neutral',
  hint: 'This record predates the processing-state column.',
};

export function scanState(s: string | null | undefined): ScanStateDesc {
  return (s == null ? undefined : STATES[s]) ?? UNRECORDED;
}
